<template>
  <Dialog :common-dialog="dialogStatus" :dialog-width="750">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100">
        <div class="d-flex align-center">
          <v-avatar color="cyan lighten-5" size="40" class="mr-3">
            <v-icon color="cyan">mdi-call-split</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-700 font-size-18 custom-header-blue-text">
              Visit Quantity Allocation Detail
            </div>
            <div class="caption grey--text text--darken-1 font-weight-500">
              Allocated quantity distribution and serial numbers across visits
            </div>
          </div>
        </div>
        <v-btn icon small @click="$emit('close:dialog')">
          <v-icon color="#000">mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <template v-slot:body>
      <!-- Product Summary Banner Card -->
      <v-card
        flat
        class="cyan darken-2 white--text rounded-lg pa-3 mb-4 d-flex align-center justify-space-between elevation-0"
      >
        <div class="d-flex align-center">
          <v-avatar
            rounded
            size="44"
            class="mr-3"
            style="background-color: rgba(255, 255, 255, 0.2)"
          >
            <v-icon color="white" large>mdi-package-variant-closed</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-700 font-size-16">
              {{ transactionDetail.product || "Product Information" }}
            </div>
            <div class="font-size-12 mt-1" style="opacity: 0.85">
              SKU:
              {{ transactionDetail.barcode || transactionDetail.sku || "N/A" }}
              · Ordered Qty: {{ totalQuantity }}
            </div>
          </div>
        </div>
        <div class="text-right pl-3">
          <div class="font-weight-700 font-size-28 line-height-1">
            {{ totalVisitAllocated }}
          </div>
          <div
            class="font-size-10 font-weight-700 text-uppercase mt-1"
            style="opacity: 0.8"
          >
            VISIT QTY
          </div>
        </div>
      </v-card>

      <!-- Destination Visits List -->
      <div class="allocation-destinations mb-4">
        <div class="d-flex align-center justify-space-between mb-2 px-1">
          <span class="caption font-weight-700 text-uppercase grey--text text--darken-2">
            Visit Allocations ({{ destinations.length }})
          </span>
        </div>

        <div v-if="destinations.length">
          <v-card
            v-for="(dest, idx) in destinations"
            :key="dest.id || idx"
            flat
            class="custom-grey-border rounded-lg mb-3 overflow-hidden"
          >
            <div class="pa-3 hover-grey-bg">
              <div class="d-flex align-center justify-space-between w-100 pr-2">
                <div class="d-flex align-center min-width-0 mr-2">
                  <v-avatar size="36" rounded class="mr-3 flex-shrink-0" color="cyan lighten-5">
                    <v-icon small color="cyan">mdi-map-marker-outline</v-icon>
                  </v-avatar>
                  <div class="min-width-0">
                    <div class="font-weight-700 font-size-14 grey--text text--darken-3 text-truncate">
                      {{ dest.title }}
                    </div>
                    <div class="font-size-12 grey--text text--darken-1 text-truncate">
                      {{ formatVisitDate(dest.subtitle) }}
                      <span v-if="dest.customer_name" class="font-weight-700 ml-1 grey--text text--darken-2">
                        · {{ dest.customer_name }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="d-flex align-center ga-2 flex-shrink-0">
                  <v-chip
                    v-if="isSerializedItem"
                    x-small
                    color="cyan darken-2"
                    text-color="white"
                    label
                    class="font-weight-700 text-uppercase"
                  >
                    <v-icon x-small left>mdi-pound</v-icon>
                    {{ getVisitSerials(dest, idx).length }} Serials
                  </v-chip>
                  <v-chip
                    v-else
                    x-small
                    color="grey lighten-2"
                    text-color="grey darken-3"
                    label
                    class="font-weight-700 text-uppercase"
                  >
                    Non-Serialized
                  </v-chip>
                  <v-chip color="cyan lighten-5" text-color="cyan darken-3" class="font-weight-700 px-3" small>
                    {{ dest.allocated || 0 }} Units
                  </v-chip>
                </div>
              </div>

              <div class="pt-3 pb-2">
                <div class="d-flex align-center justify-space-between mb-1 font-size-11">
                  <span class="grey--text text--darken-2 font-weight-600">Allocation Share</span>
                  <span class="font-weight-700 color-custom-blue">{{ calcVisitPercentage(dest, idx) }}%</span>
                </div>
                <v-progress-linear
                  :value="calcVisitPercentage(dest, idx)"
                  color="cyan"
                  background-color="grey lighten-3"
                  height="6"
                  rounded
                ></v-progress-linear>
              </div>
            </div>
          </v-card>
        </div>
        <div
          v-else
          class="pa-4 text-center rounded-lg custom-grey-border grey lighten-5"
        >
          <v-icon color="grey lighten-1" large class="mb-1"
            >mdi-map-marker-off-outline</v-icon
          >
          <div class="font-size-13 font-weight-600 grey--text text--darken-1">
            No visit allocations found for this line item.
          </div>
        </div>

        <!-- Static Warehouse Allocation -->
        <v-card
          flat
          class="custom-grey-border rounded-lg overflow-hidden"
        >
          <div class="pa-3 d-flex align-center justify-space-between">
            <div class="d-flex align-center min-width-0">
              <v-avatar size="36" rounded class="mr-3 amber lighten-4">
                <v-icon small color="amber darken-3">mdi-package-variant</v-icon>
              </v-avatar>
              <div class="min-width-0">
                <div
                  class="font-weight-700 font-size-14 grey--text text--darken-3"
                >
                  Warehouse
                </div>
                <div class="font-size-12 grey--text text--darken-1 text-truncate">
                  <!-- Buffer stock · 24 Tuas Ave 3 · Internal Stock -->
                   {{transactionDetail.company_address || ""}}
                </div>
              </div>
            </div>
            <div class="d-flex align-center ga-2 flex-shrink-0 pr-3">
              <v-chip
                color="cyan lighten-5"
                text-color="cyan darken-3"
                class="font-weight-700 px-3"
                small
              >
                {{ warehouseQuantity }} Units
              </v-chip>
              <v-icon
                small
                aria-hidden="true"
                style="visibility: hidden"
                >mdi-chevron-down</v-icon
              >
            </div>
          </div>
          <div class="pt-0 pb-2 pl-3 pr-9">
            <div
              class="d-flex align-center justify-space-between mb-1 font-size-11"
            >
              <span class="grey--text text--darken-2 font-weight-600"
                >Allocation Share</span
              >
              <span class="font-weight-700 color-custom-blue"
                >{{ calcQuantityPercentage(warehouseQuantity) }}%</span
              >
            </div>
            <v-progress-linear
              :value="calcQuantityPercentage(warehouseQuantity)"
              color="cyan"
              background-color="grey lighten-3"
              height="6"
              rounded
            ></v-progress-linear>
          </div>
        </v-card>
      </div>

      <!-- Overall Summary Card -->
      <v-card
        flat
        class="rounded-lg pa-3 mb-3 custom-grey-border grey lighten-5"
      >
        <div
          class="d-flex align-center justify-space-between mb-2 font-size-13"
        >
          <span class="font-weight-700 grey--text text--darken-3">
            Allocated {{ totalVisitAllocated }} of {{ totalQuantity }} Units to
            Visits
          </span>
          <span class="font-weight-700 grey--text text--darken-2">
            Unallocated: {{ remainingQty }}
          </span>
        </div>
        <v-progress-linear
          :value="
            Math.min(100, (totalVisitAllocated / (totalQuantity || 1)) * 100)
          "
          color="cyan"
          background-color="grey lighten-3"
          height="8"
          rounded
        ></v-progress-linear>
      </v-card>

      <!-- Status Alert Banner -->
      <!-- v-if="remainingQty === 0" -->
      <div
        v-if="false"
        class="pa-3 rounded-lg border d-flex align-center"
        style="background-color: #e8f5e9; border-color: #c8e6c9 !important"
      >
        <v-icon color="#2e7d32" small class="mr-2"
          >mdi-check-circle-outline</v-icon
        >
        <span class="font-size-13" style="color: #1b5e20">
          <strong class="font-weight-700"
            >Fully allocated across visits.</strong
          >
          Every ordered unit is assigned to scheduled visits.
        </span>
      </div>

      <div
        v-if="false"
        class="pa-3 rounded-lg border d-flex align-center"
        style="background-color: #e0f7fa; border-color: #b2ebf2 !important"
      >
        <v-icon color="#00838f" small class="mr-2"
          >mdi-information-outline</v-icon
        >
        <span class="font-size-13" style="color: #006064">
          <strong class="font-weight-700"
            >{{ remainingQty }} unit{{
              remainingQty > 1 ? "s" : ""
            }}
            unallocated.</strong
          >
          Units are allocated across {{ destinations.length }} visit location{{
            destinations.length > 1 ? "s" : ""
          }}.
        </span>
      </div>
    </template>

    <template v-slot:action>
      <v-btn
        depressed
        class="custom-grey-border custom-bold-button px-5"
        @click="$emit('close:dialog')"
      >
        Close
      </v-btn>
    </template>
  </Dialog>
</template>

<script>
import moment from "moment";
import Dialog from "@/view/pages/partials/Dialog.vue";

export default {
  name: "View-Allocation-Detail",
  components: {
    Dialog,
  },
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    transactionDetail: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activePanel: null,
    };
  },
  computed: {
    destinations() {
      const rawList = Array.isArray(this.transactionDetail?.allocations)
        ? this.transactionDetail.allocations
        : [];

      // Filter out Central Warehouse or any warehouse items completely
      return rawList.filter((d) => {
        if (d.isWarehouse) return false;
        const titleLower = String(d.title || "").toLowerCase();
        if (titleLower.includes("warehouse")) return false;
        return Number(d.allocated || 0) > 0;
      });
    },
    isSerializedItem() {
      const row = this.transactionDetail || {};
      const isGoodOrPart =
        row.product_type == "goods" ||
        row.product_type == "parts" ||
        row.type == "product" ||
        row.type == "parts" ||
        !row.product_type;
      const isSerialized =
        row.isSerialized == 1 ||
        row.serial_number == 1 ||
        (row.main_product &&
          (row.main_product.isSerialized == 1 ||
            row.main_product.trackSerial == 1 ||
            row.main_product.serial_number == 1)) ||
        (Array.isArray(row.serialize_number) &&
          row.serialize_number.length > 0);
      return isGoodOrPart && isSerialized;
    },
    totalQuantity() {
      return Number(this.transactionDetail?.quantity || 0);
    },
    totalVisitAllocated() {
      return this.destinations.reduce(
        (sum, d) => sum + (Number(d.allocated) || 0),
        0
      );
    },
    remainingQty() {
      return Math.max(0, this.totalQuantity - this.totalVisitAllocated);
    },
    warehouseQuantity() {
      return this.remainingQty;
    },
  },
  methods: {
    formatVisitDate(value) {
      if (!value) return "";

      const formatted = moment(value).format("ddd DD MMM");
      return formatted === "Invalid date" ? value : formatted;
    },
    calcVisitPercentage(dest) {
      if (
        dest &&
        typeof dest === "object" &&
        dest.percentage !== undefined &&
        dest.percentage !== null
      ) {
        return Number(dest.percentage);
      }
      const val =
        typeof dest === "object" ? dest?.allocated || 0 : Number(dest || 0);
      const total = this.totalQuantity || 1;
      return Math.round((val / total) * 100);
    },
    calcQuantityPercentage(quantity) {
      if (!this.totalQuantity) return 0;
      return Math.round((Number(quantity) / this.totalQuantity) * 100);
    },
    getSerialString(sItem) {
      if (!sItem) return "";
      if (typeof sItem === "string") return sItem;
      return sItem.serial_number || sItem.serial_no || sItem.serial || "N/A";
    },
    getVisitSerials(dest, index) {
      if (Array.isArray(dest?.serial_numbers) && dest.serial_numbers.length) {
        return dest.serial_numbers;
      }
      if (Array.isArray(dest?.serials) && dest.serials.length) {
        return dest.serials;
      }

      // If serials attached to line item level, partition slice for each visit based on allocated count
      const globalSerials = Array.isArray(
        this.transactionDetail?.serialize_number
      )
        ? this.transactionDetail.serialize_number
        : [];

      if (!globalSerials.length) return [];

      let offset = 0;
      for (let i = 0; i < index; i++) {
        offset += Number(this.destinations[i]?.allocated || 0);
      }
      const count = Number(dest.allocated || 0);
      return globalSerials.slice(offset, offset + count);
    },
  },
};
</script>

<style scoped>
.line-height-1 {
  line-height: 1;
}
.custom-border-top {
  border-top: 1px solid #e0e0e0 !important;
}
.hover-grey-bg:hover {
  background-color: #fafafa;
}
</style>
